import React, { useEffect, useState } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../auth/AuthContext'

interface MeetingBlock {
  meeting_id:   string
  class_id:     string
  class_name:   string
  kind:         'group' | 'office_hours'
  scheduled_at: string
  duration_min: number
  format:       string
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/**
 * Compact week-strip view of the mentor/admin's upcoming meetings.
 * Defaults to "this week" Mon-Sat. Click a meeting to open its class.
 *
 * Data scope follows role: admin/OM see all; mentor/instructor see meetings
 * they host or are class_mentor for.
 */
export default function WeekScheduleStrip() {
  const { session, role }    = useAuth()
  const navigate             = useNavigate()
  const [blocks, setBlocks]  = useState<MeetingBlock[]>([])
  const [loading, setLoading] = useState(true)

  // Compute Mon..Sat of current week in PT. Use the user's local clock for
  // the boundaries — close enough for an at-a-glance strip.
  function weekRange() {
    const now = new Date()
    const day = now.getDay()                    // 0=Sun..6=Sat
    const offsetToMon = day === 0 ? -6 : 1 - day
    const mon = new Date(now)
    mon.setDate(now.getDate() + offsetToMon)
    mon.setHours(0, 0, 0, 0)
    const sun = new Date(mon)
    sun.setDate(mon.getDate() + 7)
    return { from: mon.toISOString(), to: sun.toISOString(), monDate: mon }
  }

  async function load() {
    if (!session) return
    setLoading(true)
    try {
      const { from, to } = weekRange()
      // Resolve class scope for the user
      let classIds: string[] = []
      if (role === 'admin' || role === 'office_manager') {
        const { data: classes } = await supabase.from('class').select('class_id')
        classIds = (classes ?? []).map(c => c.class_id)
      } else {
        const { data: cm } = await supabase.from('class_mentor').select('class_id').eq('user_id', session.user.id)
        classIds = (cm ?? []).map(r => r.class_id)
      }
      if (classIds.length === 0) { setBlocks([]); setLoading(false); return }

      const { data: meetings } = await supabase
        .from('class_meeting')
        .select('meeting_id, class_id, kind, scheduled_at, duration_min, format')
        .in('class_id', classIds)
        .neq('status', 'cancelled')
        .gte('scheduled_at', from)
        .lt('scheduled_at', to)
        .order('scheduled_at')

      // Resolve class names
      const { data: classes } = classIds.length > 0
        ? await supabase.from('class').select('class_id, name').in('class_id', classIds)
        : { data: [] }
      const classMap = Object.fromEntries((classes ?? []).map((c: any) => [c.class_id, c.name]))

      setBlocks((meetings ?? []).map(m => ({
        meeting_id:   m.meeting_id,
        class_id:     m.class_id,
        class_name:   classMap[m.class_id] ?? '—',
        kind:         m.kind,
        scheduled_at: m.scheduled_at,
        duration_min: m.duration_min,
        format:       m.format,
      })))
    } finally {
      setLoading(false)
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [session, role])

  const { monDate } = weekRange()
  const days: Date[] = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(monDate)
    d.setDate(monDate.getDate() + i)
    return d
  })

  function blocksForDay(day: Date) {
    return blocks.filter(b => {
      const d = new Date(b.scheduled_at)
      return d.getFullYear() === day.getFullYear()
        && d.getMonth() === day.getMonth()
        && d.getDate() === day.getDate()
    })
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>This Week</Typography>
        <Typography
          variant="caption"
          sx={{ color: 'primary.main', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          onClick={() => navigate('/schedule')}
        >
          Open my schedule →
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
      ) : blocks.length === 0 ? (
        <Box sx={{
          p: 2.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
          bgcolor: 'background.paper', textAlign: 'center',
        }}>
          <Typography variant="body2" color="text.secondary">
            No meetings this week.{' '}
            <Box component="span"
              onClick={() => navigate('/schedule')}
              sx={{ color: 'primary.main', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            >
              See full schedule →
            </Box>
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 1 }}>
          {days.map((d, i) => {
            const dayBlocks = blocksForDay(d)
            const isToday   = d.toDateString() === new Date().toDateString()
            return (
              <Box key={i} sx={{
                p: 1, borderRadius: 1.25,
                border: '1px solid', borderColor: isToday ? 'primary.main' : 'divider',
                bgcolor: isToday ? 'rgba(25,118,210,0.04)' : '#fff',
                minHeight: 90,
              }}>
                <Typography variant="caption" sx={{
                  fontWeight: 700, fontSize: '0.7rem',
                  color: isToday ? 'primary.main' : 'text.secondary',
                  display: 'block', mb: 0.5,
                }}>
                  {DAY_LABELS[i]} {d.getDate()}
                </Typography>
                {dayBlocks.length === 0 ? (
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>—</Typography>
                ) : (
                  dayBlocks.map(b => (
                    <Box
                      key={b.meeting_id}
                      onClick={() => navigate(`/classes/${b.class_id}`)}
                      sx={{
                        cursor: 'pointer',
                        p: 0.75, mb: 0.5, borderRadius: 0.75,
                        bgcolor: b.kind === 'office_hours' ? 'rgba(200,151,58,0.08)' : 'rgba(15,43,76,0.06)',
                        border: '1px solid',
                        borderColor: b.kind === 'office_hours' ? 'secondary.light' : 'primary.light',
                        '&:hover': { borderColor: b.kind === 'office_hours' ? 'secondary.main' : 'primary.main' },
                      }}
                    >
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, fontSize: '0.7rem', lineHeight: 1.2 }}>
                        {new Date(b.scheduled_at).toLocaleString('en-US', {
                          hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles',
                        })}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', color: 'text.secondary', lineHeight: 1.2 }} noWrap>
                        {b.class_name}
                      </Typography>
                    </Box>
                  ))
                )}
              </Box>
            )
          })}
        </Box>
      )}
    </Box>
  )
}
